<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="audit-logs-cs-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="audit-log-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'is_public_search'">
                            <CheckCircleOutlined v-if="text == '1'" class="text-default-green" />
                            <MinusOutlined v-if="text == '0'" />
                        </template>
                        <template v-else-if="column.key === 'created_at'">{{ displayDateTime(text) }}</template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { CheckCircleOutlined, MinusOutlined } from '@ant-design/icons-vue';

export default {
    components: { CheckCircleOutlined, MinusOutlined },
    data() {
        return {
            data: [],
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
        };
    },
    props: {
        audit_id: String,
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.audit_logs.headers.searched_by'),
                    key: 'user_name',
                    dataIndex: 'user_name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.search_criteria'),
                    key: 'search_criteria',
                    dataIndex: 'search_criteria',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.result_count'),
                    key: 'result_count',
                    dataIndex: 'result_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.created_at'),
                    key: 'created_at',
                    dataIndex: 'created_at',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.audit_logs.headers.is_public_search'),
                    key: 'is_public_search',
                    dataIndex: 'is_public_search',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/audit-logs/cover-searches/' + this.audit_id, params)
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                    this.selected_row_keys = [];
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVACSLF02' }),
                        });
                    }
                });
        },
        displayDateTime(datetime) {
            return this.$dayjs(datetime).format('YYYY-MM-DD HH:mm:ss');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
    },
};
</script>
